import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface Admin {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: {
    kindergartens: string[];
    children: string[];
    parents: string[];
    menus: string[];
    cameras: string[];
    invites: string[];
    admins: string[];
  };
}

interface AdminAuthContextType {
  admin: Admin | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  hasPermission: (resource: keyof Admin['permissions'], action: string) => boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

// Налаштування axios для адміністраторів
const adminAxios = axios.create({
  baseURL: 'https://api.smartik.com.ua/api',
  withCredentials: true
});

// Додаємо токен до всіх запитів
adminAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Обробка помилок автентифікації
adminAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url: string = error?.config?.url || '';
    const isSessionCheck = url.includes('/admin-auth/me');
    if (status === 401 && isSessionCheck) {
      localStorage.removeItem('adminToken');
      window.location.href = '/login';
      return;
    }
    return Promise.reject(error);
  }
);

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('adminToken'));
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string) => {
    try {
      const response = await adminAxios.post('/admin-auth/login', { email, password });
      const { token: newToken, admin: adminData } = response.data.data;
      
      localStorage.setItem('adminToken', newToken);
      setToken(newToken);
      setAdmin(adminData);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Помилка входу');
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setAdmin(null);
  };

  const checkAuth = async () => {
    const storedToken = localStorage.getItem('adminToken');
    if (storedToken) {
      try {
        const response = await adminAxios.get('/admin-auth/me');
        setAdmin(response.data.data);
        setToken(storedToken);
      } catch (error) {
        localStorage.removeItem('adminToken');
        setToken(null);
        setAdmin(null);
      }
    }
    setLoading(false);
  };

  const hasPermission = (resource: keyof Admin['permissions'], action: string): boolean => {
    if (!admin) return false;
    const permissions = admin.permissions[resource];
    return permissions ? permissions.includes(action) : false;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AdminAuthContextType = {
    admin,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!admin,
    loading,
    hasPermission
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}; 